import type { Module } from './types';
import { IconCheck, IconCircle, IconDot } from './icons';

interface Props {
  modules: Module[];
  copiedPromptIds: Set<string>;
}

export function ProgressTracker({ modules, copiedPromptIds }: Props) {
  const summaries = modules.map((m) => {
    const copiedCount = m.prompt_ids.filter((id) => copiedPromptIds.has(id)).length;
    return { module: m, copiedCount };
  });
  const inProgress = summaries.filter(
    (s) => s.copiedCount > 0 && s.copiedCount < s.module.prompt_ids.length,
  ).length;
  const complete = summaries.filter((s) => s.copiedCount === s.module.prompt_ids.length).length;
  const active = inProgress + complete;

  return (
    <section className="progress-tracker">
      <div className="progress-tracker-header">
        <div className="progress-tracker-label">Your progress</div>
        <div className="progress-tracker-summary">
          <strong>{active}</strong> of {modules.length} sections engaged
          {complete > 0 ? (
            <>
              {' '}
              · <strong>{complete}</strong> complete
            </>
          ) : null}
        </div>
      </div>
      <div className="progress-pills">
        {summaries.map(({ module, copiedCount }) => {
          const total = module.prompt_ids.length;
          const isComplete = copiedCount === total && total > 0;
          const isInProgress = copiedCount > 0 && !isComplete;
          const classes = ['progress-pill'];
          if (isComplete) classes.push('is-complete');
          else if (isInProgress) classes.push('is-in-progress');
          const Icon = isComplete ? IconCheck : isInProgress ? IconDot : IconCircle;
          const shortLabel = module.subtitle
            .replace(/\s*·.*$/, '')
            .replace(/^Core Message.*$/i, 'Core Msg');
          return (
            <div
              key={module.module_id}
              className={classes.join(' ')}
              data-module={module.module_id}
            >
              <Icon className="progress-pill-icon" aria-hidden="true" />
              <div className="progress-pill-label">{shortLabel}</div>
              <div className="progress-pill-count">
                {copiedCount} / {total}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
